import {inject} from '@angular/core';
import {CanActivateFn} from '@angular/router';
import {map, take} from 'rxjs';

import {environment} from '../../../environments/environment';
import {ApiUser} from '../api/api.types';
import {AuthService} from '../services/auth.service';
import {BrowserNavigation} from '../services/browser-navigation.service';

/**
 * Checks whether a user has applicant privileges (individual or corporate).
 * Non-applicants (admin, agent, newspaper, institution) are excluded.
 */
export function isApplicantUser(user: ApiUser | null | undefined): boolean {
  if (!user) return false;

  const role = user.role?.trim().toLowerCase();
  const userType = user.user_type?.trim().toLowerCase();

  // Explicitly excluded from applicant portal
  if (
    role === 'admin' ||
    userType === 'admin' ||
    role === 'agent' ||
    userType === 'agent' ||
    role === 'newspaper' ||
    userType === 'newspaper' ||
    role === 'institution' ||
    userType === 'institution'
  ) {
    return false;
  }

  return (
    role === 'applicant' ||
    role === 'individual' ||
    role === 'corporate' ||
    userType === 'individual' ||
    userType === 'corporate'
  );
}

/**
 * Restricts all /app/* routes in npr_portal to applicant users (individual and corporate).
 *
 * If an admin, agent, newspaper, or institution user accesses these routes,
 * they are redirected to npr_auth without logging out. npr_auth's guestGuard
 * automatically detects their active session and routes them to their role-appropriate destination.
 */
export const roleGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const navigation = inject(BrowserNavigation);

  const checkAccess = (user: ApiUser | null): boolean => {
    if (!user) {
      // Unauthenticated access is handled upstream by authGuard.
      return false;
    }

    if (isApplicantUser(user)) {
      return true;
    }

    // Redirect non-applicants to auth without logging out; auth's guestGuard handles role routing.
    navigation.redirect(environment.authUrl);
    return false;
  };

  if (auth.ready()) {
    return checkAccess(auth.user());
  }

  return auth.ensureSession().pipe(
    take(1),
    map((user) => checkAccess(user))
  );
};
